import { useState, useCallback, useRef, useEffect } from 'react';

export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  visible: boolean;
}

export interface UseSelectionBoxProps {
  containerRef: React.RefObject<HTMLElement>;
  itemRefs: Map<string, HTMLElement>;
  onSelectionChange: (selectedIds: string[]) => void;
  enabled?: boolean;
}

export interface UseSelectionBoxReturn {
  selectionBox: SelectionBox;
  handleMouseDown: (event: React.MouseEvent) => void;
}

/**
 * Hook for click-drag selection box
 * Draws a selection rectangle and selects items that intersect with it
 */
export function useSelectionBox({
  containerRef,
  itemRefs,
  onSelectionChange,
  enabled = true,
}: UseSelectionBoxProps): UseSelectionBoxReturn {
  const [selectionBox, setSelectionBox] = useState<SelectionBox>({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    visible: false,
  });

  const isDraggingRef = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled || !containerRef.current) return;

      // Only start selection box on left click and not on interactive elements
      if (event.button !== 0) return;

      const target = event.target as HTMLElement;
      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'A' ||
        target.closest('button, input, a, [role="button"]');

      if (isInteractive) return;

      const rect = containerRef.current.getBoundingClientRect();
      const startX = event.clientX - rect.left;
      const startY = event.clientY - rect.top;

      isDraggingRef.current = true;
      startPositionRef.current = { x: startX, y: startY };

      setSelectionBox({
        startX,
        startY,
        endX: startX,
        endY: startY,
        visible: false, // Only show after minimum drag distance
      });
    },
    [enabled, containerRef]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const endX = event.clientX - rect.left;
      const endY = event.clientY - rect.top;

      const { x: startX, y: startY } = startPositionRef.current;

      // Only show selection box after dragging at least 5px
      const distance = Math.sqrt(
        Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)
      );
      const visible = distance > 5;

      setSelectionBox({
        startX,
        startY,
        endX,
        endY,
        visible,
      });

      // Calculate intersecting items
      if (visible && itemRefs.size > 0) {
        const selectedIds: string[] = [];

        // Get selection box bounds (normalized)
        const boxLeft = Math.min(startX, endX);
        const boxRight = Math.max(startX, endX);
        const boxTop = Math.min(startY, endY);
        const boxBottom = Math.max(startY, endY);

        // Check each item for intersection
        itemRefs.forEach((element, id) => {
          const itemRect = element.getBoundingClientRect();
          const containerRect = containerRef.current!.getBoundingClientRect();

          // Convert item bounds to container-relative coordinates
          const itemLeft = itemRect.left - containerRect.left;
          const itemRight = itemRect.right - containerRect.left;
          const itemTop = itemRect.top - containerRect.top;
          const itemBottom = itemRect.bottom - containerRect.top;

          // Check intersection
          const intersects =
            itemLeft < boxRight &&
            itemRight > boxLeft &&
            itemTop < boxBottom &&
            itemBottom > boxTop;

          if (intersects) {
            selectedIds.push(id);
          }
        });

        onSelectionChange(selectedIds);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setSelectionBox(prev => ({ ...prev, visible: false }));
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [enabled, containerRef, itemRefs, onSelectionChange]);

  return {
    selectionBox,
    handleMouseDown,
  };
}
